/* Copyright © 2026 YOUR GROWTH 365 (Jonas Brössel) — Proprietary. See LICENSE in repo root. */
/* =============================================================================
   VIEWS / ADMIN / ORDERS-DATA — Konstanten + pure Selektoren/Sortierung
   =============================================================================
   S201 REF-7e: aus orders.js extrahiert (Null Verhaltensänderung). Reine
   Daten-/Auswahl-Schicht ohne DOM — der Vitest-Kern des Bestell-Boards:
     · COLUMNS / STATUS_LABEL / STATUS_BADGE_CLASS (Board-Konstanten)
     · todayISO / minutesUntilPickup / orderSortByPickup (pure)
     · getActiveOrders / getFinishedToday / getCarryOverOrders (Cache-Selektoren)
     · ensureActiveItemsLoaded (Lazy-Enrich der order_items, Memory
       feedback_gg_storage_orders_list_no_items / gg-microtask-render-loop)
   Cross-Module-Nutzung: orders.js (Orchestrator) + orders-cards.js importieren
   von hier; dieses Modul importiert KEINEN Orchestrator (acyclisch).
============================================================================ */

import { shortTime, dateToISO, snapshotHasRenderableItems } from '../../utils/helpers.js';
import { isOrderPaid, isOrderSettled } from '../../core/order-statuses.js';
import { hasCachedOrPendingItems, batchFetchItemsFor } from '../../core/order-items-cache.js';
import { rerenderActiveTab } from '../../utils/dom.js';

function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { orders: [], settings: {} };
}

// -----------------------------------------------------------------------------
// Board-Konstanten
// -----------------------------------------------------------------------------

// S60 FIX.5 — 4-Spalten-Kanban. Die „Kassieren"-Spalte enthält Orders, deren
// Speisen ausgegeben sind (status='picked_up'), aber die Zahlung noch nicht
// erfasst wurde. `requireUnpaid:true` triggert den Zusatzfilter im Spalten-Filter.
export const COLUMNS = [
  { id: 'incoming',   label: 'Eingehend',      statuses: ['pending', 'confirmed'] },
  { id: 'preparing',  label: 'In Zubereitung', statuses: ['preparing'] },
  { id: 'ready',      label: 'Bereit',         statuses: ['ready'] },
  { id: 'cashout',    label: 'Kassieren',      statuses: ['picked_up'], requireUnpaid: true }
];

export const STATUS_LABEL = {
  pending:   'offen',
  confirmed: 'angenommen',
  preparing: 'in Zubereitung',
  ready:     'bereit',
  picked_up: 'abgeholt',
  cancelled: 'storniert',
  no_show:   'No-Show'
};

export const STATUS_BADGE_CLASS = {
  pending:   'badge-pending',
  confirmed: 'badge-confirmed',
  preparing: 'badge-manual',
  ready:     'badge-completed',
  picked_up: 'badge-completed',
  cancelled: 'badge-declined',
  no_show:   'badge-declined'
};

// -----------------------------------------------------------------------------
// Date/Time-Helpers
// -----------------------------------------------------------------------------
export function todayISO() {
  return dateToISO(new Date());
}

/** Minuten bis Abholzeit (negativ = überfällig). Null wenn keine Zeit. */
export function minutesUntilPickup(order) {
  const date = order && (order.pickupDate || order.pickup_date);
  const time = shortTime(order && (order.pickupTime || order.pickup_time));
  if (!date || !time) return null;
  const target = new Date(date + 'T' + time + ':00');
  if (Number.isNaN(target.getTime())) return null;
  return Math.round((target.getTime() - Date.now()) / 60000);
}

/** Sortierung: aufsteigend nach Abholzeit, ohne Zeit ans Ende. */
export function orderSortByPickup(a, b) {
  const ta = shortTime(a.pickupTime || a.pickup_time);
  const tb = shortTime(b.pickupTime || b.pickup_time);
  if (!ta && !tb) return 0;
  if (!ta) return 1;
  if (!tb) return -1;
  return ta.localeCompare(tb);
}

// -----------------------------------------------------------------------------
// Order-Cache-Filter (pure Selektoren)
// -----------------------------------------------------------------------------

export function getActiveOrders() {
  const state = getState();
  const arr = Array.isArray(state.orders) ? state.orders : [];
  const today = todayISO();
  // S60 FIX.5 — Aktive Orders umfassen jetzt auch picked_up-aber-unbezahlte
  // (Spalte „Kassieren"). Erst payment_status='paid' macht eine Order „erledigt".
  // Datum-Filter: pickup_date >= today (Mitternachts-Spätschicht erlaubt).
  return arr.filter(o => {
    if (!o) return false;
    const date = o.pickupDate || o.pickup_date;
    if (!date) return false;
    const s = o.status;
    // Lebende Pipeline
    if (s === 'pending' || s === 'confirmed' || s === 'preparing' || s === 'ready') {
      return date >= today; // alte Pipeline-Leichen ignorieren
    }
    // Kassieren-Spalte: serviert, aber Bezahlung offen — IMMER sichtbar,
    // auch wenn pickupDate vor heute liegt (Bonloch-Schutz, GoBD).
    if (s === 'picked_up' && !isOrderPaid(o)) return true;
    return false;
  }).sort(orderSortByPickup);
}

export function getFinishedToday() {
  const state = getState();
  const arr = Array.isArray(state.orders) ? state.orders : [];
  const today = todayISO();
  // S60 FIX.5 — Erledigt nur, wenn entweder bezahlt (picked_up + paid) oder
  // storniert / No-Show. picked_up-aber-unbezahlt gehört in „Kassieren".
  return arr.filter(o => {
    if (!o) return false;
    const date = o.pickupDate || o.pickup_date;
    if (date !== today) return false;
    return isOrderSettled(o);
  }).sort(orderSortByPickup);
}

/** S100-A — Carry-Over: offene Pipeline-Orders vergangener Tage (zustandsgetrieben,
 *  nicht datumsgetrieben: bleibt offen bis abgeschlossen/storniert; nie per
 *  Mitternacht aus der Sicht). NICHT picked_up-unbezahlt (→ „Kassieren", datumslos)
 *  und NICHT cancelled/no_show/finished. Ohne pickup_date nicht „überfällig". */
export function getCarryOverOrders() {
  const state = getState();
  const arr = Array.isArray(state.orders) ? state.orders : [];
  const today = todayISO();
  return arr.filter(o => {
    if (!o) return false;
    const date = o.pickupDate || o.pickup_date;
    if (!date) return false;          // ohne Datum nicht „überfällig"
    if (date >= today) return false;  // heute/Zukunft → normale Pipeline (Kanban)
    const s = o.status;
    return s === 'pending' || s === 'confirmed' || s === 'preparing' || s === 'ready';
  }).sort(orderSortByPickup);
}

// -----------------------------------------------------------------------------
// S233 Ticket B/C — Unbezahlte Dine-In-Orders sichtbar machen (S232-Leak-Schutz)
// -----------------------------------------------------------------------------
// Der S232-Vorfall: eine dine-in-Order (status 'ready', unpaid, KEIN pickup_date)
// hing 5 Wochen an einer Belegung und war nirgends sichtbar — sie fällt durch alle
// datumsbasierten Board-Filter (getActiveOrders/getCarryOverOrders prüfen
// pickup_date, das dine-in nicht trägt). Diese Selektoren greifen deshalb am
// created_at (Europe/Berlin), nicht am pickup_date.

/** Bruttobetrag einer Order in Cent (tolerant camel/snake). */
export function orderGrossCents(o) {
  if (!o) return 0;
  return Number(o.totalGrossCents != null ? o.totalGrossCents : o.total_gross_cents) || 0;
}

/** Summe der Bruttobeträge (Cent) über eine Order-Liste. */
export function sumOrderGrossCents(orders) {
  return (orders || []).reduce((s, o) => s + orderGrossCents(o), 0);
}

/** Pure-Prädikat: „offene, unbezahlte Dine-In-Order" — dine-in, nicht bezahlt,
 *  nicht storniert/no_show, keine Korrektur. Basis für Ticket B + Ticket C. */
export function isOpenUnpaidDineIn(o) {
  if (!o) return false;
  const svc = o.serviceType || o.service_type;
  if (svc !== 'dine-in') return false;
  if (isOrderPaid(o)) return false;
  if (o.isCorrection) return false;
  const s = o.status;
  return s !== 'cancelled' && s !== 'no_show';
}

/** Berlin-Kalendertag (YYYY-MM-DD) einer Order — created_at-basiert, weil dine-in
 *  keinen pickup_date trägt (genau die S232-Lücke). */
function orderCreatedDay(o) {
  const created = o && (o.createdAt || o.created_at);
  if (!created) return '';
  try { return dateToISO(new Date(created)); } catch (_) { return ''; }
}

/** S233 Ticket B — offene, unbezahlte Dine-In-Orders aus VORTAGEN (created_at
 *  Europe/Berlin < heute). */
export function getStaleUnpaidDineInOrders() {
  const arr = Array.isArray(getState().orders) ? getState().orders : [];
  const today = todayISO();
  return arr.filter(o => {
    if (!isOpenUnpaidDineIn(o)) return false;
    const day = orderCreatedDay(o);
    return !!day && day < today;
  }).sort((a, b) => String(a.createdAt || a.created_at || '').localeCompare(String(b.createdAt || b.created_at || '')));
}

/** S233 Ticket C — offene, unbezahlte Dine-In-Orders HEUTE + Vortage (für den
 *  nicht-blockierenden Z-Bon-Hinweis „Trotzdem abschließen?"). */
export function getOpenUnpaidDineInOrders() {
  const arr = Array.isArray(getState().orders) ? getState().orders : [];
  return arr.filter(isOpenUnpaidDineIn)
    .sort((a, b) => String(a.createdAt || a.created_at || '').localeCompare(String(b.createdAt || b.created_at || '')));
}

/**
 * S104-A — Lazy-Load der order_items für die sichtbaren Orders über das gemeinsame
 * Item-Access-Modul (core/order-items-cache.js). Modul-Cache keyed by orderId,
 * überlebt den Realtime-Re-Sync; Guard = Cache-Präsenz + Inflight (Memory
 * gg-microtask-render-loop: inflight-aware, sonst Microtask-Render-Loop). Genau EIN
 * nicht-destruktives Re-Render, sobald neue Items geladen sind.
 */
export function ensureActiveItemsLoaded(orders) {
  const s = window.App && window.App.storage && window.App.storage.orders;
  // Ohne Lade-Funktion kein Caching → der Re-Render unten würde dieselben
  // „missing" IDs erneut anfragen (Endlos-Schleife). Wie der alte Enrich abbrechen.
  if (!s || (typeof s.listItemsForOrderIds !== 'function' && typeof s.listItems !== 'function')) return;
  // S105 — inflight-IDs ausschließen, nicht nur gecachte (hasCachedOrPendingItems
  // deckt beide ab → ein Fetch je Order, genau ein Re-Render, wenn er ankommt).
  const ids = (orders || [])
    .filter(o => o && o.id && !hasCachedOrPendingItems(o.id)
                 && !snapshotHasRenderableItems(o.preorderSnapshot || o.preorder_snapshot))
    .map(o => o.id);
  if (!ids.length) return;
  batchFetchItemsFor(ids)
    .then(() => rerenderActiveTab())
    .catch(e => console.warn('[orders] ensureActiveItemsLoaded fehlgeschlagen (nicht-fatal):', e));
}
